"use client";

import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type LocationItem = { name: string; description?: string };

type LocationsProps = {
  heading: string;
  items: LocationItem[];
  locale?: Locale;
};

export const LocationsSection = ({
  heading,
  items,
  locale = "fr",
}: LocationsProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  return (
    <section className="relative overflow-hidden bg-white py-14 text-black">
      <div className="pointer-events-none absolute -left-20 top-8 h-44 w-44 rounded-full bg-primary/15 blur-[90px]" />
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {ui.locationsEyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-2 font-space-age text-xl tracking-[0.2em]"
        >
          {heading}
        </motion.h2>
        {/* Helper accessibilite (invisible) */}
        <p className="sr-only">
          {ui.locationsSrPrefix}
          {items.map((i) => i.name).join(", ")}.
        </p>
        <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((l, idx) => (
            <motion.li
              key={l.name}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.35, delay: idx * 0.04 }}
              whileHover={{ y: -3 }}
              className="group rounded-2xl border border-black/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-md"
            >
              <div className="mb-1 text-sm font-semibold">{l.name}</div>
              {l.description && (
                <p className="text-sm text-black/70">{l.description}</p>
              )}
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default LocationsSection;
